/**
 * Todo 作成時のプロジェクト選択バッジ
 *
 * 既存タスクに付いているプロジェクトを並べ、その場で新しいプロジェクト名も追加できる。
 */

import { FolderOpen, Plus } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import type { TodoResponse } from '@/api/types/todo'

type Props = {
  /** プロジェクト候補を集めるタスク一覧 */
  todos: TodoResponse[]
  value: string | null
  onChange: (project: string | null) => void
  /** プロジェクト名入力欄を Enter / Escape で閉じたとき (入力欄の外へのフォーカス移動では呼ばない) */
  onInputClose?: () => void
}

export const ProjectPicker = ({ todos, value, onChange, onInputClose }: Props) => {
  // Todo に付けるまでは保存されないため、ここで追加したプロジェクトは画面上だけで持つ
  const [extraProjects, setExtraProjects] = useState<string[]>([])
  const [isAdding, setIsAdding] = useState(false)
  const [newProject, setNewProject] = useState('')
  // 入力欄が消えるときの blur で、取り消した名前を確定しないためのフラグ
  const inputClosedRef = useRef(false)

  const projects = useMemo(() => {
    const set = new Set<string>(extraProjects)
    for (const t of todos) {
      if (t.project) set.add(t.project)
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'ja'))
  }, [todos, extraProjects])

  const openInput = () => {
    inputClosedRef.current = false
    setIsAdding(true)
  }

  const cancelInput = () => {
    inputClosedRef.current = true
    setIsAdding(false)
    setNewProject('')
  }

  const submitInput = () => {
    if (inputClosedRef.current) return
    inputClosedRef.current = true
    const name = newProject.trim()
    setIsAdding(false)
    setNewProject('')
    if (!name) return
    setExtraProjects((prev) => (prev.includes(name) ? prev : [...prev, name]))
    onChange(name)
  }

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
      <FolderOpen className="size-3 text-muted-foreground shrink-0" />
      {projects.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(value === p ? null : p)}
          className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full border transition-colors max-w-32 truncate ${
            value === p
              ? 'border-primary/50 bg-primary/10 text-primary'
              : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40'
          }`}
        >
          {p}
        </button>
      ))}
      {isAdding ? (
        <input
          autoFocus
          value={newProject}
          onChange={(e) => setNewProject(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return
            // 先に閉じてフラグを立ててから、フォーカス移動で起きる blur による二重確定を防ぐ
            if (e.key === 'Enter') {
              e.preventDefault()
              submitInput()
              onInputClose?.()
            }
            if (e.key === 'Escape') {
              cancelInput()
              onInputClose?.()
            }
          }}
          onBlur={submitInput}
          placeholder="プロジェクト名"
          className="shrink-0 w-28 text-[11px] px-2 py-0.5 rounded-full border border-primary/50 bg-transparent outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={openInput}
          className="shrink-0 inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/50 transition-colors"
        >
          <Plus className="size-3" />
          追加
        </button>
      )}
    </div>
  )
}
